import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, MonitorSmartphone, ShieldCheck, XCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const SCOPE_LABELS = {
  'endpoint:identity.read': 'Read your Nova identity & workspace',
  'endpoint:local.scan': 'Run local security scans on this device',
  'endpoint:local.results.read': 'Read saved local scan results',
  'llm:invoke': 'Run code-review AI through Nova Cloud',
};

const EndpointActivate = () => {
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get('user_code') || '').toUpperCase());
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // 'approved' | 'denied'
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceId, setWorkspaceId] = useState('');

  useEffect(() => {
    try {
      const ws = JSON.parse(localStorage.getItem('workspaces') || 'null')
        || JSON.parse(localStorage.getItem('user') || '{}')?.workspaces || [];
      const cur = JSON.parse(localStorage.getItem('current_workspace') || 'null');
      setWorkspaces(Array.isArray(ws) ? ws : []);
      if (cur?.id) setWorkspaceId(cur.id);
      else if (Array.isArray(ws) && ws[0]?.id) setWorkspaceId(ws[0].id);
    } catch { /* noop */ }
  }, []);

  const lookup = useCallback(async (c) => {
    const clean = (c || '').trim().toUpperCase();
    if (!clean) return;
    setLoading(true); setPending(null);
    try {
      const r = await axios.get('/api/endpoint/device/pending', { params: { user_code: clean } });
      setPending(r.data);
    } catch (e) {
      setPending(null);
      toast.error(e?.response?.status === 404 ? 'Code not found or expired' : 'Lookup failed');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (params.get('user_code')) lookup(params.get('user_code')); }, [params, lookup]);

  const approve = async () => {
    if (!workspaceId) { toast.error('Select a workspace'); return; }
    setBusy(true);
    try {
      await axios.post('/api/endpoint/device/approve', new URLSearchParams({ user_code: code.trim().toUpperCase(), workspace_id: workspaceId }));
      setDone('approved');
      toast.success('Device approved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approval failed');
    } finally { setBusy(false); }
  };

  const deny = async () => {
    setBusy(true);
    try {
      await axios.post('/api/endpoint/device/deny', new URLSearchParams({ user_code: code.trim().toUpperCase() }));
      setDone('denied');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Deny failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-lg border-slate-200 shadow-sm" data-testid="endpoint-activate">
        <CardHeader>
          <div className="flex items-center gap-2 text-slate-900">
            <MonitorSmartphone className="w-5 h-5" />
            <CardTitle className="text-lg">Connect Nova Endpoint</CardTitle>
          </div>
          <CardDescription>Authorize a Nova Endpoint installation to act on your behalf in a workspace.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {done === 'approved' && (
            <div className="flex items-center gap-2 text-emerald-700 text-sm" data-testid="activate-approved">
              <CheckCircle2 className="w-4 h-4" /> Device approved. Return to Nova Endpoint — it will finish signing in automatically.
            </div>
          )}
          {done === 'denied' && (
            <div className="flex items-center gap-2 text-red-700 text-sm" data-testid="activate-denied">
              <XCircle className="w-4 h-4" /> Request denied. You can close this page.
            </div>
          )}

          {!done && (
            <>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Device code</label>
                <div className="flex gap-2">
                  <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="XXXX-XXXX" className="font-mono tracking-widest" data-testid="activate-code-input" />
                  <Button variant="outline" onClick={() => lookup(code)} disabled={loading} data-testid="activate-lookup-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Look up'}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Enter the code shown in the Nova Endpoint app.</p>
              </div>

              {pending && (
                <div className="rounded-lg border border-slate-200 p-4 space-y-4" data-testid="activate-details">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{pending.device_name || 'Nova Endpoint'}</div>
                      <div className="text-xs text-slate-500">{pending.platform || 'unknown platform'}</div>
                    </div>
                    <Badge variant="secondary" className="text-[11px]">awaiting approval</Badge>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Requested permissions</div>
                    <ul className="space-y-1">
                      {(pending.scopes || []).map((s) => (
                        <li key={s} className="flex items-start gap-2 text-sm text-slate-700">
                          <ShieldCheck className="w-4 h-4 text-emerald-500 mt-0.5" />
                          <span>{SCOPE_LABELS[s] || s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-700">Workspace</label>
                    <Select value={workspaceId} onValueChange={setWorkspaceId}>
                      <SelectTrigger data-testid="activate-workspace-select"><SelectValue placeholder="Select a workspace" /></SelectTrigger>
                      <SelectContent>
                        {workspaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">The installation is bound to this one workspace.</p>
                  </div>

                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="ghost" onClick={deny} disabled={busy} data-testid="activate-deny-btn">Deny</Button>
                    <Button onClick={approve} disabled={busy || !workspaceId} data-testid="activate-approve-btn">
                      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                      Approve device
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EndpointActivate;
